// The business map drawn: units and roles on top (the org chart), processes
// in the middle, systems and data stores below. Edges come from process
// steps (role → process, process → system), role membership (role → unit),
// data use (system → data store) and initiatives ring their targets.
import React, { useMemo } from 'react';
import { ReactFlow, Background, Controls, MarkerType } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { SPECS } from './fields';

const W = 180; const GAP = 24;
const row = (items, y, color, kind, extra = () => '') => items.map((it, i) => ({
  id: `${kind}:${it.id}`,
  position: { x: i * (W + GAP), y },
  data: { label: (<div className="bp-node"><span className="bp-node-kind" style={{ color }}>{SPECS[kind]?.label || kind}</span><strong>{it.name}</strong><span className="bp-node-sub">{extra(it)}</span></div>) },
  style: { width: W, border: `1px solid ${color}55`, borderRadius: 10, background: 'var(--bg-elev)', color: 'var(--text)', padding: 6, fontSize: 12 },
}));

export default function BlueprintMap({ systems = [], dataStores = [], processes = [], units = [], roles = [], initiatives = [], onPick }) {
  const { nodes, edges } = useMemo(() => {
    const nodes = [
      ...row(units, 0, SPECS.orgUnit.accent, 'orgUnit', (u) => `${u.headcount || 0} people`),
      ...row(roles, 120, SPECS.orgRole.accent, 'orgRole', (r) => `${r.headcount || 1} × ${r.loadedAnnualCostUsd ? `$${Math.round(r.loadedAnnualCostUsd / 1000)}k` : 'no rate'}`),
      ...row(processes, 260, SPECS.bizProcess.accent, 'bizProcess', (p) => `${(p.steps || []).length} steps · ${p.volumePerMonth || 0}/mo`),
      ...row(systems, 400, SPECS.bizSystem.accent, 'bizSystem', (s) => `${s.category || ''}${s.annualCostUsd ? ` · $${Math.round(s.annualCostUsd / 1000)}k/yr` : ''} · ${s.verdict || 'undecided'}`),
      ...row(dataStores, 520, SPECS.dataStore.accent, 'dataStore', (d) => `${d.kind || ''}${d.sizeGb ? ` · ${d.sizeGb} GB` : ''}`),
    ];
    const edges = [];
    const push = (a, b, color, label) => { if (!a || !b) return; const id = `${a}->${b}`; if (edges.some((e) => e.id === id)) return; edges.push({ id, source: a, target: b, label, style: { stroke: color, opacity: 0.7 }, markerEnd: { type: MarkerType.ArrowClosed, color } }); };
    for (const u of units) if (u.parentId) push(`orgUnit:${u.parentId}`, `orgUnit:${u.id}`, SPECS.orgUnit.accent);
    for (const r of roles) {
      if (r.unitId) push(`orgUnit:${r.unitId}`, `orgRole:${r.id}`, SPECS.orgUnit.accent);
      for (const pid of r.processIds || []) push(`orgRole:${r.id}`, `bizProcess:${pid}`, SPECS.orgRole.accent);
      for (const sid of r.systemIds || []) push(`orgRole:${r.id}`, `bizSystem:${sid}`, SPECS.orgRole.accent);
    }
    for (const p of processes) {
      if (p.ownerUnitId) push(`orgUnit:${p.ownerUnitId}`, `bizProcess:${p.id}`, SPECS.orgUnit.accent, 'owns');
      for (const s of p.steps || []) {
        if (s.roleId) push(`orgRole:${s.roleId}`, `bizProcess:${p.id}`, SPECS.orgRole.accent);
        if (s.systemId) push(`bizProcess:${p.id}`, `bizSystem:${s.systemId}`, SPECS.bizProcess.accent);
      }
    }
    for (const d of dataStores) for (const sid of d.systemIds || []) push(`bizSystem:${sid}`, `dataStore:${d.id}`, SPECS.bizSystem.accent);
    // Initiatives ring their targets.
    const ringed = new Set();
    for (const it of initiatives.filter((i) => !['rejected', 'done'].includes(i.status))) {
      const t = it.targets || {};
      for (const id of t.systemIds || []) ringed.add(`bizSystem:${id}`);
      for (const id of t.processIds || []) ringed.add(`bizProcess:${id}`);
      for (const id of t.roleIds || []) ringed.add(`orgRole:${id}`);
      for (const id of t.dataStoreIds || []) ringed.add(`dataStore:${id}`);
    }
    for (const n of nodes) if (ringed.has(n.id)) n.style = { ...n.style, boxShadow: '0 0 0 2px #c084fc' };
    return { nodes, edges };
  }, [systems, dataStores, processes, units, roles, initiatives]);

  if (!nodes.length) return <div className="cs-empty">Nothing mapped yet. Add a system, a process and a role — or tell NoVA "map my business".</div>;
  return (
    <div className="bp-map" data-nova-ignore="true">
      <ReactFlow nodes={nodes} edges={edges} fitView fitViewOptions={{ padding: 0.2 }} nodesConnectable={false} proOptions={{ hideAttribution: true }} onNodeClick={(_, n) => { const [kind, id] = n.id.split(':'); onPick?.(kind, id); }}>
        <Background gap={16} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
}
